import { useState, useCallback, useEffect, useMemo, type SyntheticEvent } from "react";
import { warmupAudio } from "@/lib/audio/proceduralSfx";
import { notifyUserInteraction } from "@/lib/audio/menuTheme";
import { MenuBackdrop } from "@/components/rk/MenuBackdrop";
import { getAuthHeaders, useAuth } from "@/lib/stores/useAuth";
import { apiFetch } from "@/lib/apiBase";
import { useI18n } from "@/lib/i18n";
import { LocaleSelector } from "@/components/i18n/LocaleSelector";
import { StatusNotice, type StatusNoticeTone } from "@/components/ui/StatusNotice";
import { notifyError, notifyInfo, notifySuccess } from "@/lib/ui/feedback";
import { CONTENT_RATING } from "@shared/contentRating";
import { useViewport } from "@/hooks/use-viewport";
import { Capacitor } from "@capacitor/core";

interface SplashScreenProps {
  onStart: () => void;
}

/**
 * Abre Termos/Privacidade a partir do aceite de cadastro.
 *
 * No navegador, `target="_blank"` abre uma aba e o formulário meio preenchido
 * continua vivo atrás dela — é o comportamento certo e o href faz o trabalho.
 *
 * No app nativo não existe "outra aba": o WebView entrega o _blank ao navegador
 * do sistema, que recebe `https://localhost/termos-uso` — um endereço que só
 * resolve DENTRO do aparelho. O usuário via uma página de erro em vez dos
 * termos que estava aceitando. Aqui navegamos no próprio WebView, onde os
 * assets existem; o "voltar" do Android devolve ao cadastro.
 */
function openLegalPage(event: React.MouseEvent, path: string) {
  event.stopPropagation();
  if (!Capacitor.isNativePlatform()) return;
  event.preventDefault();
  window.location.href = path;
}

export function SplashScreen({ onStart }: SplashScreenProps) {
  const { t } = useI18n();
  const { isShort } = useViewport();
  const [isPressed, setIsPressed] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [showLoginOptions, setShowLoginOptions] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [providers, setProviders] = useState<{ google: boolean; discord: boolean; apple: boolean; email: boolean } | null>(null);
  const {
    loginWithGoogle,
    loginWithDiscord,
    requestEmailLoginCode,
    verifyEmailLoginCode,
    logout,
    isLoading,
    isAuthenticated,
    user,
    error,
    clearError,
    updateProfile,
  } = useAuth();
  const [fullName, setFullName] = useState("");
  const [alias, setAlias] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [aliasValid, setAliasValid] = useState<boolean | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Verificar se o perfil está completo
  const isProfileComplete = user && user.alias && user.birthDate && user.fullName && user.email;
  const needsProfileCompletion = isAuthenticated && user && !isProfileComplete;
  const authStatus = useMemo<{ tone: StatusNoticeTone; message: string } | null>(() => {
    if (!showLoginOptions) return null;

    if (error) {
      return { tone: "error", message: error };
    }

    if (isLoading) {
      return { tone: "loading", message: "Processando autenticação..." };
    }

    if (!isAuthenticated && providers === null) {
      return { tone: "loading", message: "Verificando métodos de login disponíveis..." };
    }

    if (
      !isAuthenticated &&
      providers &&
      providers.google === false &&
      providers.discord === false &&
      providers.email === false
    ) {
      return { tone: "warning", message: "Nenhum método de login está disponível no momento." };
    }

    if (needsProfileCompletion) {
      return { tone: "info", message: "Complete seu perfil para liberar o botão Jogar Agora." };
    }

    if (isAuthenticated && isProfileComplete) {
      return { tone: "success", message: "Login concluído. Você já pode entrar no jogo." };
    }

    return null;
  }, [showLoginOptions, error, isLoading, isAuthenticated, providers, needsProfileCompletion, isProfileComplete]);

  const startGame = useCallback(() => {
    if (isStarting) return;
    setIsStarting(true);
    warmupAudio();
    notifyUserInteraction();
    setTimeout(() => {
      onStart();
    }, 150);
  }, [onStart, isStarting]);

  const handleStart = useCallback(() => {
    if (isStarting || isLoading) return;
    
    // Se não mostra opções ainda, mostrar
    if (!showLoginOptions) {
      setShowLoginOptions(true);
      return;
    }
    
    // Se não está autenticado, não faz nada
    if (!isAuthenticated) {
      return;
    }
    
    // Se precisa completar perfil, não inicia
    if (needsProfileCompletion) {
      return;
    }
    
    // Tudo OK, iniciar jogo
    startGame();
  }, [isStarting, isLoading, showLoginOptions, isAuthenticated, needsProfileCompletion, startGame]);

  const isInteractiveTarget = useCallback((target: EventTarget | null) => {
    return target instanceof Element && target.closest('[data-splash-interactive="true"]') !== null;
  }, []);

  const handleUseAnotherAccount = useCallback((event?: SyntheticEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    clearError();
    setProfileError(null);
    setShowEmailForm(false);
    setEmailCodeSent(false);
    setEmailCode("");
    setAcceptedTerms(false);
    setShowLoginOptions(true);
    logout();
    notifyInfo("Sessão encerrada. Escolha outro método de login.");
  }, [clearError, logout]);

  // Preencher campos com dados do usuário
  useEffect(() => {
    if (!user) return;
    setFullName(user.fullName || "");
    setAlias(user.alias || "");
    setBirthDate(user.birthDate || "");
    setEmail(user.email || "");
  }, [user]);

  // Resetar código quando email muda
  useEffect(() => {
    setEmailCodeSent(false);
    setEmailCode("");
  }, [email]);

  // Verificar disponibilidade do alias em tempo real
  useEffect(() => {
    if (!alias || alias.length < 3) {
      setAliasValid(null);
      return;
    }
    
    const normalized = alias.toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!/^[a-z0-9_]{3,16}$/.test(normalized)) {
      setAliasValid(false);
      return;
    }
    
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/auth/alias/check?alias=${encodeURIComponent(alias)}`);
        if (res.ok) {
          const data = await res.json();
          setAliasValid(data.available);
        }
      } catch {
        setAliasValid(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [alias]);

  // Buscar providers disponíveis
  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;

    type StatusError = Error & { status?: number };

    const loadProviders = async (attempt = 0) => {
      try {
        const res = await apiFetch("/api/auth/status", {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) {
          const err: StatusError = new Error(`Auth status request failed (${res.status})`);
          err.status = res.status;
          throw err;
        }

        const data = await res.json();
        if (cancelled) return;

        if (data?.providers) {
          setProviders({
            google: data.providers.google !== false,
            discord: data.providers.discord !== false,
            email: data.providers.email !== false,
            apple: data.providers.apple === true,
          });
          return;
        }

        throw new Error("Auth status payload missing providers");
      } catch (error) {
        if (cancelled) return;

        const status = typeof error === "object" && error && "status" in error
          ? Number((error as { status?: unknown }).status)
          : null;

        // Em 429 usar backoff mais longo para não amplificar o rate limit.
        if (status === 429 && attempt < 4) {
          const nextDelayMs = (attempt + 1) * 2500;
          retryTimer = window.setTimeout(() => loadProviders(attempt + 1), nextDelayMs);
          return;
        }

        if (attempt < 2) {
          const nextDelayMs = (attempt + 1) * 600;
          retryTimer = window.setTimeout(() => loadProviders(attempt + 1), nextDelayMs);
          return;
        }

        // Keep providers unknown when request fails to avoid false "coming soon".
        console.warn("[Auth] Failed to load provider status:", error);
        setProviders(null);
      }
    };

    loadProviders();

    return () => {
      cancelled = true;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (providers?.email === false) {
      setShowEmailForm(false);
      setEmailCodeSent(false);
      setEmailCode("");
    }
  }, [providers?.email]);

  // Função para salvar perfil
  const handleSaveProfile = async () => {
    setProfileError(null);
    
    // Validações
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setProfileError(t("auth.invalidEmail"));
      return;
    }
    if (!fullName || fullName.trim().length < 3) {
      setProfileError(t("auth.fullNameMinLength"));
      return;
    }
    if (!alias || !/^[a-zA-Z0-9_]{3,16}$/.test(alias)) {
      setProfileError(t("auth.aliasMinLength"));
      return;
    }
    if (!birthDate) {
      setProfileError(t("auth.birthDateRequired"));
      return;
    }
    
    setIsSavingProfile(true);
    
    try {
      const response = await apiFetch("/api/auth/profile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ fullName, alias, birthDate, email }),
      });

      if (!response.ok) {
        const data = await response.json();
        setProfileError(data.error || t("auth.profileSaveFailed"));
        return;
      }

      const data = await response.json();
      updateProfile(data.user);
      notifySuccess("Perfil salvo com sucesso.");
      
      // Perfil completo, iniciar jogo
      startGame();
    } catch {
      notifyError(t("auth.connectionError"));
      setProfileError(t("auth.connectionError"));
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    /**
     * Três faixas em coluna — idioma, conteúdo, rodapé legal — em vez de
     * um bloco centralizado com o rodapé em `absolute`.
     *
     * Deitado, os 390px de altura não comportavam o conteúdo centralizado,
     * e como o rodapé estava fora do fluxo ele não empurrava nada: o botão
     * "Toque para Jogar" caía por cima de "Termos de Uso · Política de
     * Privacidade" e da versão. Aviso legal atrás de um botão não é aviso.
     * Agora o miolo é a única faixa elástica e rola quando falta espaço;
     * idioma e rodapé têm altura própria e ninguém sobrepõe ninguém.
     */
    <div
      className="fixed inset-0 z-50 flex select-none flex-col overflow-hidden"
      onClick={(e) => {
        if (isInteractiveTarget(e.target)) return;
        handleStart();
      }}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={(e) => {
        setIsPressed(false);
        if (isInteractiveTarget(e.target)) return;
        handleStart();
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {/* Fundo persistente (céu + véu + vinheta + aurora) */}
      <MenuBackdrop />

      {/* Faixa do seletor de idioma. Estava em `absolute top-2 right-2`
          e, deitado, as bandeiras caíam por cima do "…ANKS" do wordmark —
          o título é `whitespace-nowrap` e ocupa a largura toda. Numa
          faixa própria as duas coisas coexistem. */}
      <div
        className="relative z-20 flex shrink-0 justify-end px-2 pt-[max(6px,env(safe-area-inset-top))] pr-[max(8px,env(safe-area-inset-right))]"
        data-splash-interactive="true"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); }}
      >
        <LocaleSelector showNames={false} className="gap-1" />
      </div>

      {/* Miolo — a única faixa elástica, e a única que rola. */}
      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center overflow-y-auto overscroll-contain scroll-smooth-ios px-4 py-4 short:py-2">
        {/* Deitado vira duas colunas: marca e classificação de um lado,
            ação do outro. É a mesma troca que o resto do app faz — em
            paisagem a largura sobra e a altura falta. */}
        <div className="mx-auto grid w-full max-w-lg gap-6 short:max-w-4xl short:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] short:items-center short:gap-x-8 short:gap-y-2">
          {/* ---------- Coluna: identidade ---------- */}
          <div className="flex flex-col items-center gap-6 short:gap-3">
            {/* Logo/Title */}
            <div>
              <div className="relative inline-block">
                <div
                  className="absolute inset-0 blur-xl opacity-70 animate-pulse"
                  style={{
                    background: 'linear-gradient(90deg, #22d3ee, #ec4899, #facc15)',
                    animation: 'glow-pulse 2s ease-in-out infinite',
                  }}
                />
                {/* O tamanho vem do JS, não de `short:text-4xl`: um
                    telefone deitado tem 844px de largura e casa com
                    `md:`, que venceria a variante de altura. Era o
                    wordmark em 60px atravessando a tela e passando por
                    baixo das bandeiras do seletor de idioma. */}
                <h1
                  className={`relative font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-yellow-400 tracking-wide whitespace-nowrap ${
                    isShort ? "text-4xl" : "text-4xl sm:text-5xl md:text-6xl"
                  }`}
                >
                  ROCKETANKS
                </h1>
              </div>
            </div>

            {/* Rocket icon */}
            <div className="text-5xl animate-bounce short:text-4xl" style={{ animationDuration: "2s" }}>
              🚀
            </div>

            {/* Classificação indicativa — Lei Felca (art. 8º): exibida com
                destaque no momento do acesso, no padrão visual do MJSP */}
            <div className="flex items-center gap-3 rounded-xl border border-white/25 bg-black/50 px-4 py-2.5 short:px-3 short:py-2">
              <span
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-green-600 text-white text-2xl font-black short:h-9 short:w-9 short:text-xl"
                aria-label={CONTENT_RATING.ratingLabel}
              >
                {CONTENT_RATING.rating}
              </span>
              <div className="text-left">
                <p className="text-white text-xs font-bold uppercase tracking-wide">
                  {CONTENT_RATING.ratingLabel}
                </p>
                <p className="text-white/70 text-[11px]">{CONTENT_RATING.descriptors.join(" · ")}</p>
              </div>
            </div>
          </div>

          {/* ---------- Coluna: ação ---------- */}
          <div className="flex w-full flex-col items-center gap-6 short:gap-3">
        {/* Estado: Tela inicial - apenas botão de iniciar */}
        {!showLoginOptions && (
          <div
            className={`
              relative px-6 py-5 min-[360px]:px-10 rounded-2xl cursor-pointer
              bg-gradient-to-r from-cyan-500 to-cyan-600
              shadow-lg shadow-cyan-500/30
              transition-all duration-150 ease-out
              short:w-full short:text-center
              ${isPressed ? "scale-95 shadow-sm" : "hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/40"}
            `}
          >
            <span className="text-xl sm:text-2xl font-bold text-white tracking-wide">
              {t("auth.tapToPlay")}
            </span>
            <div className="absolute inset-0 rounded-2xl bg-white/20 animate-ping opacity-20" />
          </div>
        )}

        {/* Estado: Opções de Login */}
        {showLoginOptions && !isAuthenticated && (
          <div className="w-full space-y-4 pointer-events-auto" data-splash-interactive="true" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-2">
              <h2 className="text-white text-lg font-semibold">{t("auth.welcome")}</h2>
              <p className="text-white/60 text-sm">{t("auth.chooseLoginMethod")}</p>
            </div>

            {authStatus && (
              <StatusNotice
                tone={authStatus.tone}
                message={authStatus.message}
                className="text-left"
              />
            )}

            <div className="space-y-3">
              {/* Google */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (providers?.google === false) return;
                  loginWithGoogle();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                }}
                className={`w-full px-5 py-4 rounded-xl font-bold flex items-center gap-4 transition-all ${
                  providers?.google === false
                    ? "bg-slate-700/50 text-white/40 cursor-not-allowed"
                    : "bg-white text-slate-900 hover:bg-gray-100 hover:scale-[1.02] active:scale-[0.98]" 
                }`}
                disabled={isLoading || providers?.google === false}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="flex-1 text-left">{t("auth.loginWithGoogle")}</span>
                {providers?.google === false && (
                  <span className="text-xs bg-white/10 px-2 py-1 rounded-full">{t("auth.comingSoon")}</span>
                )}
              </button>

              {/* Discord */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (providers?.discord === false) return;
                  loginWithDiscord();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                }}
                className={`w-full px-5 py-4 rounded-xl font-bold flex items-center gap-4 transition-all ${
                  providers?.discord === false
                    ? "bg-slate-700/50 text-white/40 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-500 hover:scale-[1.02] active:scale-[0.98]" 
                }`}
                disabled={isLoading || providers?.discord === false}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                <span className="flex-1 text-left">{t("auth.loginWithDiscord")}</span>
                {providers?.discord === false && (
                  <span className="text-xs bg-white/10 px-2 py-1 rounded-full">{t("auth.comingSoon")}</span>
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 h-px bg-white/20" />
                <span className="text-white/40 text-sm">{t("common.or")}</span>
                <div className="flex-1 h-px bg-white/20" />
              </div>

              {/* Email toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (providers?.email === false) return;
                  setShowEmailForm((prev) => !prev);
                }}
                className={`w-full px-5 py-4 rounded-xl font-semibold flex items-center gap-4 transition-all ${
                  providers?.email === false
                    ? "bg-slate-700/50 text-white/40 cursor-not-allowed"
                    :
                  showEmailForm 
                    ? "bg-cyan-600 text-white" 
                    : "bg-slate-800/80 text-white hover:bg-slate-700/80"
                }`}
                disabled={isLoading || providers?.email === false}
              >
                <span className="text-xl">✉️</span>
                <span className="flex-1 text-left">{t("auth.loginWithEmail")}</span>
                {providers?.email === false && (
                  <span className="text-xs bg-white/10 px-2 py-1 rounded-full">{t("auth.unavailable")}</span>
                )}
                <span className="text-white/60">{showEmailForm ? "▲" : "▼"}</span>
              </button>
            </div>

            {/* Email Form */}
            {showEmailForm && providers?.email !== false && (
              <div className="bg-slate-900/80 border border-white/10 rounded-xl p-5 space-y-4 mt-3">
                <p className="text-white/70 text-sm">
                  {emailCodeSent 
                    ? t("auth.emailCodeSent") 
                    : t("auth.emailCodePrompt")}
                </p>
                
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      className="w-full px-4 py-3 pl-11 rounded-xl bg-slate-800 text-white border border-white/10 focus:border-cyan-500 focus:outline-none transition-colors disabled:opacity-50"
                      placeholder={t("auth.yourEmail")}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={emailCodeSent}
                    />
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">📧</span>
                  </div>
                  
                  {emailCodeSent && (
                    <div className="relative">
                      <input
                        className="w-full px-4 py-4 rounded-xl bg-slate-800 text-white text-center tracking-[0.5em] text-2xl font-mono border border-white/10 focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="• • • • • •"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        autoFocus
                      />
                    </div>
                  )}
                  
                  {profileError && (
                    <div className="text-red-400 text-sm text-center bg-red-500/10 py-2 rounded-lg">{profileError}</div>
                  )}

                  {/* Checkbox de termos - apenas no primeiro passo */}
                  {!emailCodeSent && (
                    <label className="flex items-start gap-3 p-3 rounded-xl bg-slate-800/50 border border-white/10 cursor-pointer hover:border-white/20 transition-colors">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="w-5 h-5 mt-0.5 rounded border-white/30 bg-slate-700 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                      />
                      <span className="text-white/70 text-xs leading-relaxed">
                        {t("auth.termsAgree")}{" "}
                        <a
                          href="/termos-uso"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 underline"
                          onClick={(e) => openLegalPage(e, "/termos-uso")}
                        >
                          {t("auth.termsOfUse")}
                        </a>{" "}
                        {t("common.and")}{" "}
                        <a
                          href="/politica-privacidade"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:text-cyan-300 underline"
                          onClick={(e) => openLegalPage(e, "/politica-privacidade")}
                        >
                          {t("auth.privacyPolicy")}
                        </a>
                      </span>
                    </label>
                  )}
                  
                  {!emailCodeSent ? (
                    <button
                      className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:from-cyan-400 hover:to-cyan-500 transition-all"
                      disabled={!email || !acceptedTerms || isLoading}
                      onClick={async () => {
                        setProfileError(null);
                        await requestEmailLoginCode(email);
                        const latestError = useAuth.getState().error;
                        if (!latestError) {
                          setEmailCodeSent(true);
                          notifySuccess("Código enviado para seu e-mail.");
                        } else {
                          notifyError(latestError);
                        }
                      }}
                    >
                      {isLoading ? t("auth.sending") : t("auth.sendCode")}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <button
                        className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:from-emerald-400 hover:to-emerald-500 transition-all"
                        disabled={emailCode.length !== 6 || isLoading}
                        onClick={async () => {
                          setProfileError(null);
                          await verifyEmailLoginCode(email, emailCode);
                          const latestError = useAuth.getState().error;
                          if (latestError) {
                            notifyError(latestError);
                          } else {
                            notifySuccess("Login realizado com sucesso.");
                          }
                        }}
                      >
                        {isLoading ? t("auth.verifying") : t("auth.confirmAndLogin")}
                      </button>
                      <button
                        className="w-full py-2 text-white/50 text-sm hover:text-white/80 transition-colors"
                        onClick={() => {
                          setEmailCodeSent(false);
                          setEmailCode("");
                          setProfileError(null);
                        }}
                      >
                        {t("auth.useAnotherEmail")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Estado: Completar Perfil */}
        {needsProfileCompletion && (
          <div className="w-full space-y-4 pointer-events-auto" data-splash-interactive="true" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-2">
              <h2 className="text-white text-xl font-bold flex items-center justify-center gap-2">
                {t("auth.completeProfile")}
              </h2>
              <p className="text-white/60 text-sm mt-1">{t("auth.profileInfoNeeded")}</p>
            </div>

            {profileError && (
              <StatusNotice tone="error" message={profileError} />
            )}

            <div className="bg-slate-900/80 border border-white/10 rounded-xl p-5 space-y-4">
              {/* Email */}
              <div>
                <label className="block text-white/60 text-xs mb-1 ml-1">{t("auth.email")}</label>
                <div className="relative">
                  <input
                    className="w-full px-4 py-3 pl-11 rounded-xl bg-slate-800 text-white border border-white/10 focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder={t("auth.yourEmail")}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">📧</span>
                </div>
              </div>

              {/* Nome completo */}
              <div>
                <label className="block text-white/60 text-xs mb-1 ml-1">{t("auth.fullName")} <span className="text-white/30">{t("auth.fullNamePrivate")}</span></label>
                <div className="relative">
                  <input
                    className="w-full px-4 py-3 pl-11 rounded-xl bg-slate-800 text-white border border-white/10 focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder={t("auth.yourName")}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">👤</span>
                </div>
              </div>

              {/* Alias */}
              <div>
                <label className="block text-white/60 text-xs mb-1 ml-1">
                  {t("auth.publicAlias")} <span className="text-white/30">{t("auth.aliasChars")}</span>
                </label>
                <div className="relative">
                  <input
                    className={`w-full px-4 py-3 pl-11 pr-11 rounded-xl bg-slate-800 text-white border transition-colors focus:outline-none ${
                      aliasValid === true 
                        ? "border-emerald-500" 
                        : aliasValid === false 
                          ? "border-red-500" 
                          : "border-white/10 focus:border-cyan-500"
                    }`}
                    placeholder={t("auth.yourAlias")}
                    value={alias}
                    onChange={(e) => setAlias(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 16))}
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">@</span>
                  {aliasValid !== null && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2">
                      {aliasValid ? "✅" : "❌"}
                    </span>
                  )}
                </div>
                {aliasValid === false && alias.length >= 3 && (
                  <p className="text-red-400 text-xs mt-1 ml-1">{t("auth.aliasInUse")}</p>
                )}
              </div>

              {/* Data de nascimento */}
              <div>
                <label className="block text-white/60 text-xs mb-1 ml-1">{t("auth.birthDate")}</label>
                <div className="relative">
                  <input
                    type="date"
                    className="w-full px-4 py-3 pl-11 rounded-xl bg-slate-800 text-white border border-white/10 focus:border-cyan-500 focus:outline-none transition-colors"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">🎂</span>
                </div>
              </div>

              {/* Submit button */}
              <button
                className="w-full px-5 py-4 rounded-xl bg-gradient-to-r from-cyan-500 via-cyan-600 to-emerald-500 text-white font-bold text-lg shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-[1.02] active:scale-[0.98]"
                onClick={handleSaveProfile}
                disabled={isSavingProfile || aliasValid === false}
              >
                {isSavingProfile ? t("auth.saving") : t("auth.saveAndPlay")}
              </button>
              
              <button
                onClick={handleUseAnotherAccount}
                className="w-full py-2 text-white/50 text-sm hover:text-white/80 transition-colors"
              >
                {t("auth.useAnotherAccount")}
              </button>
            </div>
          </div>
        )}

        {/* Estado: Autenticado e perfil completo - botão para iniciar */}
        {showLoginOptions && isAuthenticated && isProfileComplete && (
          <div className="w-full space-y-4 pointer-events-auto" data-splash-interactive="true" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <p className="text-white text-lg">
                {t("auth.welcomeBack")} <span className="text-cyan-400 font-bold">@{user.alias}</span>!
              </p>
            </div>
            
            <button
              onClick={startGame}
              className={`
                w-full px-8 py-5 rounded-2xl cursor-pointer
                bg-gradient-to-r from-cyan-500 to-emerald-500
                shadow-lg shadow-cyan-500/30
                transition-all duration-150 ease-out
                hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/50
                active:scale-[0.98]
                ${isStarting ? "opacity-70" : ""}
              `}
            >
              <span className="text-xl sm:text-2xl font-bold text-white tracking-wide flex items-center justify-center gap-3">
                {isStarting ? t("auth.starting") : t("auth.playNow")}
              </span>
            </button>
            
            <button
              onClick={handleUseAnotherAccount}
              className="w-full py-2 text-white/50 text-sm hover:text-white/80 transition-colors"
            >
              {t("auth.useAnotherAccount")}
            </button>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Rodapé legal — no fluxo, não em `absolute`. Deitado, os links e a
          versão entram na mesma linha: são duas alturas de texto que o
          miolo recupera. */}
      <div className="relative z-10 flex shrink-0 flex-col items-center gap-2 px-4 pb-[max(10px,env(safe-area-inset-bottom))] pt-2 short:flex-row short:justify-center short:gap-3 short:pt-1">
        <div
          className="flex items-center gap-3 text-xs"
          data-splash-interactive="true"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button 
            type="button"
            className="text-slate-500 hover:text-slate-400 transition-colors bg-transparent border-none cursor-pointer p-1 -m-1"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              window.location.href = '/termos-uso';
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              window.location.href = '/termos-uso';
            }}
          >
            {t("auth.termsOfUse")}
          </button>
          <span className="text-slate-700">•</span>
          <button 
            type="button"
            className="text-slate-500 hover:text-slate-400 transition-colors bg-transparent border-none cursor-pointer p-1 -m-1"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              window.location.href = '/politica-privacidade';
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              window.location.href = '/politica-privacidade';
            }}
          >
            {t("auth.privacyPolicy")}
          </button>
        </div>
        <div className="text-slate-600 text-xs">
          v2.0.0
        </div>
      </div>
    </div>
  );
}
